import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { NavLink } from 'react-router';
import clsx from 'clsx';
/**
 * Prop-driven left-rail navigation. Glass background, JetBrains Mono
 * uppercase 11px section titles, pill-shaped nav items with a
 * configurable active-row accent so PlatformShell (lav / royal) and
 * ProductShell (per-product accent) can share this one component.
 *
 * Callers supply the `sections` array; an optional `header` slot renders
 * above the first section — used by ProductShell to show the
 * "← Back to One" chip + product label.
 */
export function Sidebar({ sections, header, 
/**
 * CSS colour (or var reference like `var(--royal)`) used as the active
 * row's accent: border tint, icon colour, and a soft gradient fill.
 * Defaults to the Chaosology royal purple.
 */
accentColor = 'var(--royal)', 
/** Soft fill used behind the active row; usually a tint of the accent. */
accentSoftColor = 'var(--lav-50)', accentBorderColor = 'var(--lav-200)', }) {
    return (_jsxs("aside", { className: "overflow-y-auto overscroll-contain border-r border-[var(--line)] px-3 pt-[18px] pb-6 sticky top-[68px] self-start h-[calc(100vh-68px)]", style: {
            gridArea: 'side',
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(22px) saturate(140%)',
            WebkitBackdropFilter: 'blur(22px) saturate(140%)',
            // Expose accents as CSS vars so NavItem can read them without
            // needing inline styles on every row.
            '--sidebar-accent': accentColor,
            '--sidebar-accent-soft': accentSoftColor,
            '--sidebar-accent-border': accentBorderColor,
        }, "aria-label": "Primary", children: [header && _jsx("div", { className: "mb-4", children: header }), sections.map((section) => (_jsxs("div", { className: "mb-[22px]", children: [_jsx("div", { className: "px-[14px] pt-1 pb-2.5 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-500)]", style: { fontFamily: 'var(--f-mono)' }, children: section.title }), _jsx("nav", { className: "flex flex-col gap-0.5", children: section.items.map((item) => (_jsx(NavItem, { item: item }, item.to))) })] }, section.title)))] }));
}
/* -------- sub-components -------- */
function NavItem({ item }) {
    const { Icon, label, to, badge, dim, end } = item;
    // Dimmed items are rendered as a static row — no navigation, reduced
    // opacity, no hover affordance. Matches `.nav-item.dim` in the mockup.
    if (dim) {
        return (_jsxs("div", { className: "flex items-center gap-3 px-[14px] py-[9px] rounded-full text-[13.5px] font-medium text-[var(--ink-700)] opacity-55 cursor-default select-none", "aria-disabled": "true", children: [_jsx("span", { className: "grid h-[18px] w-[18px] place-items-center shrink-0", children: _jsx(Icon, { className: "h-[17px] w-[17px]", "aria-hidden": true }) }), _jsx("span", { children: label }), badge && _jsx(Badge, { badge: badge })] }));
    }
    return (_jsxs(NavLink, { to: to, end: end ?? to === '/', className: ({ isActive }) => clsx('relative flex items-center gap-3 rounded-full px-[14px] py-[9px] text-[13.5px] font-medium text-[var(--ink-700)] no-underline transition-[background,color,transform,box-shadow]', 'hover:bg-[rgba(11,13,35,0.04)] hover:text-[var(--ink-900)]', isActive &&
            'border px-[13px] py-[8px] text-[var(--ink-900)] shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_2px_8px_-4px_rgba(11,13,35,0.18)]'), style: ({ isActive }) => isActive
            ? {
                borderColor: 'var(--sidebar-accent-border)',
                background: 'linear-gradient(180deg, var(--sidebar-accent-soft), var(--white))',
                // Icon + text accent picked up by the child span via color inherit
                // — set the nav-icon colour through a style property on the link.
                ['--nav-icon-color']: 'var(--sidebar-accent)',
            }
            : undefined, children: [_jsx("span", { className: "grid h-[18px] w-[18px] place-items-center shrink-0 nav-icon", style: { color: 'var(--nav-icon-color, inherit)' }, children: _jsx(Icon, { className: "h-[17px] w-[17px]", "aria-hidden": true }) }), _jsx("span", { children: label }), badge && _jsx(Badge, { badge: badge })] }));
}
function Badge({ badge }) {
    const { text, kind = 'count' } = badge;
    const kindClass = {
        count: 'bg-white border-[var(--line)] text-[var(--ink-900)] shadow-[var(--shadow-xs)]',
        new: 'border-[rgba(20,192,142,0.3)] text-[var(--emerald-text)] [background:linear-gradient(135deg,var(--mint-100),var(--mint-200))]',
        soon: 'border-[var(--lav-200)] text-[var(--royal)] [background:linear-gradient(135deg,var(--lav-100),var(--lav-50))]',
        q4: 'border-[rgba(245,197,66,0.45)] text-[var(--gold)] [background:linear-gradient(135deg,var(--butter-100),var(--butter-50))]',
        yr: 'border-[var(--line)] bg-[rgba(11,13,35,0.05)] text-[var(--ink-400)]',
    };
    return (_jsx("span", { className: clsx('ml-auto rounded-full border px-2 py-[3px] text-[10px] font-semibold uppercase tracking-[0.06em]', `sidebar-badge sidebar-badge--${kind}`, kindClass[kind]), style: { fontFamily: 'var(--f-mono)' }, children: text }));
}
